import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingBag, Share2, Zap, Star, Check, ImageIcon, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import UrgencyBanner from "@/components/UrgencyBanner";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { formatINR } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { trackViewContent } from "@/lib/metaPixel";
import { buyNow } from "@/lib/buyNow";
import { ratingFor, reviewCountFor } from "@/lib/ratings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { handle } = useParams();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await fetch(`${API}/shop/product/${handle}`);
        if (!res.ok) {
          if (!cancelled) setNotFound(true);
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (!data.product) setNotFound(true);
        else {
          setProduct(data.product);
          trackViewContent({
            id: data.product.handle,
            name: data.product.title,
            price: data.product.price,
          });
        }
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const handleAdd = () => {
    if (!product || soldOut) return;
    addItem({
      id: product.handle,
      name: product.title,
      category: "shop",
      price: Math.round(product.price || 0),
      originalPrice: product.compareAtPrice ? Math.round(product.compareAtPrice) : null,
      image: product.image,
      variantId: product.variantId,
      quantityAvailable: product.quantityAvailable,
      availableForSale: product.availableForSale,
    });
    toast.success(`${product.title} added to your festive box`, {
      description: "Express 2-Day delivery applied.",
      action: { label: "View", onClick: openCart },
    });
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: product?.title || "Diwali Gifts In Two Days",
      text: `Check out ${product?.title} — express 2-day Diwali delivery.`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (e) {
      /* user cancelled */
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share it anywhere");
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const images = product?.images && product.images.length > 0 ? product.images : product?.image ? [product.image] : [];
  const discount = product?.compareAtPrice
    ? Math.round((1 - product.price / product.compareAtPrice) * 100)
    : null;
  const stockLeft = typeof product?.quantityAvailable === "number" ? product.quantityAvailable : null;
  const soldOut = product && (product.availableForSale === false || product.currentlyNotInStock === true || stockLeft === 0);
  const lowStock = stockLeft !== null && stockLeft > 0 && stockLeft <= 5;

  return (
    <>
      <UrgencyBanner />
      <Navigation />
      <main className="min-h-screen bg-[#0B0F19] pt-[108px]">
        <div className="mx-auto max-w-7xl px-6 py-10 md:px-10 md:py-16">
          <Link
            to="/"
            data-testid="product-back-link"
            className="mb-10 inline-flex items-center gap-2 font-sans text-sm tracking-wide text-[#A9B0C2] transition-colors duration-300 hover:text-[#D4AF37]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
            Back to Boutique
          </Link>

          {loading && (
            <div className="flex min-h-[50vh] items-center justify-center font-sans text-sm text-[#8B93A7]" data-testid="product-loading">
              Curating product…
            </div>
          )}

          {notFound && !loading && (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center" data-testid="product-not-found">
              <h1 className="font-serif text-3xl text-[#F3EEE3] md:text-5xl">Product not found</h1>
              <p className="font-sans text-sm text-[#8B93A7]">The link you followed may be broken, or the piece is no longer in our atelier.</p>
              <Link to="/" className="mt-4 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-6 py-3 font-sans text-sm text-[#F3E5AB] hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19]">
                Return to home
              </Link>
            </div>
          )}

          {product && !loading && (
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16" data-testid="product-detail">
              {/* Image gallery */}
              <div className="flex flex-col gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="relative aspect-square w-full overflow-hidden rounded-xl border border-[#F3E5AB]/10 bg-[#111826]"
                >
                  {images[activeImg] ? (
                    <img
                      src={images[activeImg]}
                      alt={product.title}
                      data-testid="product-main-image"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="shimmer flex h-full w-full items-center justify-center">
                      <div className="absolute left-1/2 top-1/2 h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 bloom-gold opacity-40" />
                      <div className="relative flex flex-col items-center gap-2 text-[#D4AF37]/40">
                        <ImageIcon className="h-10 w-10" strokeWidth={1} />
                        <span className="font-sans text-[10px] uppercase tracking-[0.25em]">Image coming soon</span>
                      </div>
                    </div>
                  )}
                  <div className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 bg-[#0B0F19]/70 px-3 py-1.5 backdrop-blur-md">
                    <Zap className="h-3 w-3 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
                    <span className="font-sans text-[10px] font-medium uppercase tracking-[0.18em] text-[#F3E5AB]">Express 2-Day</span>
                  </div>
                </motion.div>

                {images.length > 1 && (
                  <div className="flex gap-3 overflow-x-auto">
                    {images.map((src, i) => (
                      <button
                        key={src + i}
                        onClick={() => setActiveImg(i)}
                        data-testid={`product-thumb-${i}`}
                        className={`relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border transition-all duration-300 ${
                          i === activeImg ? "border-[#D4AF37] shadow-[0_0_18px_rgba(212,175,55,0.35)]" : "border-[#F3E5AB]/10 hover:border-[#D4AF37]/40"
                        }`}
                      >
                        <img src={src} alt={`${product.title} ${i + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col"
              >
                <span className="font-sans text-xs uppercase tracking-[0.3em] text-[#D4AF37]">The Boutique</span>
                <h1 className="mt-4 font-serif text-4xl leading-[1.08] tracking-tight text-[#F3EEE3] md:text-5xl" data-testid="product-title">
                  {product.title}
                </h1>

                <div className="mt-5 flex items-center gap-2">
                  <Star className="h-4 w-4 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
                  <span className="font-sans text-sm text-[#8B93A7]">
                    {ratingFor(product.handle).toFixed(1)} · {reviewCountFor(product.handle)} reviews
                  </span>
                </div>

                <div className="mt-8 flex items-baseline gap-3">
                  <span className="font-serif text-4xl font-semibold text-[#D4AF37]" data-testid="product-price">
                    {formatINR(product.price)}
                  </span>
                  {product.compareAtPrice && (
                    <span className="font-sans text-lg text-[#8B93A7] line-through">{formatINR(product.compareAtPrice)}</span>
                  )}
                  {discount && <span className="rounded-full border border-[#F3E5AB]/15 px-2.5 py-1 font-sans text-xs font-medium text-[#F3E5AB]">{discount}% off</span>}
                </div>

                {product.description && (
                  <p className="mt-8 whitespace-pre-line font-sans text-base leading-relaxed text-[#A9B0C2]" data-testid="product-description">
                    {product.description}
                  </p>
                )}

                <ul className="mt-8 space-y-3 border-t border-[#F3E5AB]/10 pt-8">
                  <li className="flex items-center gap-3 font-sans text-sm text-[#E8E2D0]">
                    <Check className="h-4 w-4 text-[#D4AF37]" strokeWidth={2} />
                    Guaranteed 48-hour express delivery across major Indian cities
                  </li>
                  <li className="flex items-center gap-3 font-sans text-sm text-[#E8E2D0]">
                    <Truck className="h-4 w-4 text-[#D4AF37]" strokeWidth={1.8} />
                    Powered by Consortium Gifts · 26+ years of luxury gifting
                  </li>
                </ul>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={handleAdd}
                    data-testid="product-add-to-cart"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-6 py-4 font-sans text-sm font-medium tracking-wide text-[#F3E5AB] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B0F19]"
                  >
                    <ShoppingBag className="h-4 w-4" strokeWidth={1.8} />
                    Add to Festive Box
                  </button>
                  <button
                    onClick={() =>
                      buyNow({
                        variantId: product.variantId,
                        id: product.handle,
                        name: product.title,
                        price: product.price,
                        onStart: () => setBuying(true),
                        onEnd: () => setBuying(false),
                      })
                    }
                    disabled={buying}
                    data-testid="product-buy-now"
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#D4AF37] bg-[#D4AF37] px-6 py-4 font-sans text-sm font-semibold tracking-wide text-[#0B0F19] transition-all duration-300 hover:bg-[#F3E5AB] hover:shadow-[0_0_28px_rgba(212,175,55,0.5)] disabled:opacity-70"
                  >
                    {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" fill="#0B0F19" strokeWidth={0} />}
                    Buy Now
                  </button>
                  <button
                    onClick={handleShare}
                    data-testid="product-share"
                    className="flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 bg-transparent px-6 py-4 font-sans text-sm tracking-wide text-[#F3E5AB] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    <Share2 className="h-4 w-4" strokeWidth={1.8} />
                    Share
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}
